export interface LineaConCuenta {
  cuentaBancoOrigen?: string | null;
  montoAPagar: number;
  montoAPagarLCY: number;
  currencyCode: string | null;
}

export interface SaldoCuenta {
  cuentaBancoOrigen: string;
  saldoDisponible: number;
}

/** Agrupa las líneas con cuenta bancaria asignada, por cuenta — base de DisponibilidadCuentas. */
export function agruparPorCuenta(lineas: LineaConCuenta[], saldos: SaldoCuenta[]) {
  const porCuenta = new Map<
    string,
    { currencyCode: string | null; totalAsignado: number; totalAsignadoLCY: number }
  >();
  for (const l of lineas) {
    if (!l.cuentaBancoOrigen) continue;
    const actual = porCuenta.get(l.cuentaBancoOrigen) ?? {
      currencyCode: l.currencyCode,
      totalAsignado: 0,
      totalAsignadoLCY: 0,
    };
    actual.totalAsignado += l.montoAPagar;
    actual.totalAsignadoLCY += l.montoAPagarLCY;
    porCuenta.set(l.cuentaBancoOrigen, actual);
  }
  return [...porCuenta.entries()].map(([cuentaBancoOrigen, v]) => {
    const saldoDisponible =
      saldos.find((s) => s.cuentaBancoOrigen === cuentaBancoOrigen)?.saldoDisponible ?? 0;
    return {
      cuentaBancoOrigen,
      currencyCode: v.currencyCode,
      totalAsignado: v.totalAsignado,
      totalAsignadoLCY: v.totalAsignadoLCY,
      saldoDisponible,
      diferencia: saldoDisponible - v.totalAsignado,
    };
  });
}

/**
 * Resumen en DL/colones para el cintillo de totales: CUENTAS DL es la suma del saldo disponible
 * (anotado a mano) de las cuentas efectivamente usadas, convertido a colones; SALDO es esa misma
 * suma menos lo asignado a esas cuentas (en DL) — el mismo cálculo que la columna "Diferencia" de
 * Disponibilidad de cuentas, pero consolidado.
 *
 * Para convertir el saldo de una cuenta en moneda extranjera (ej. USD) a colones, no inventamos
 * un tipo de cambio: usamos la tasa implícita real de esa MISMA cuenta (totalAsignadoLCY /
 * totalAsignado de sus propias facturas asignadas) — así el saldo en dólares se convierte con el
 * mismo tipo de cambio que ya se usó para calcular el monto DL de esas facturas. Si hay líneas sin
 * cuenta asignada todavía, no entran en este cálculo — por diseño, igual que abajo.
 */
export function resumenSaldoCuentas(lineas: LineaConCuenta[], saldos: SaldoCuenta[]) {
  const porCuenta = agruparPorCuenta(lineas, saldos);
  let totalSaldoLCY = 0;
  let totalAsignadoLCY = 0;
  for (const c of porCuenta) {
    const tasa = c.totalAsignado !== 0 ? c.totalAsignadoLCY / c.totalAsignado : 1;
    totalSaldoLCY += c.saldoDisponible * tasa;
    totalAsignadoLCY += c.totalAsignadoLCY;
  }
  return { totalSaldo: totalSaldoLCY, totalAsignadoLCY, tieneCuentas: porCuenta.length > 0 };
}
